import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import LogoLoader from "../../common/LogoLoader";
import { usePartnerships } from "../../../hooks/queries/usePartnerships";
import { monospaceStyle } from "../../../theme";
import { PartnershipGrain, PartnershipNode, PartnershipPair, Partnerships } from "../../../types/analytics";
import { ExportFacts } from "../../../utils/export/ExportFrame";
import { useCapturing } from "../../../utils/export/ExportStage";
import { SEGMENT_SX, TOOL_BUTTON_SX } from "../../ValueChain/ValueChainControls";
import { AnalyticsPaneProps } from "../analyticsPanes";
import {
  ANALYTICS_VIEW_LABELS,
  PARTNERSHIP_CLUSTER_COUNTS,
  PARTNERSHIP_GRAINS,
  PARTNERSHIP_PER_CLUSTER_COUNTS,
  PARTNERSHIPS_CLUSTERS_DEFAULT,
  PARTNERSHIPS_CLUSTERS_MOST,
  PARTNERSHIPS_GROUPS_MOST,
  PARTNERSHIPS_MODES,
  PARTNERSHIPS_PER_CLUSTER_DEFAULT,
  PartnershipsMode,
  PartnershipsSettings,
  changePartnershipsSettings,
  toPartnershipsParams,
} from "../analyticsState";
import { counted } from "../Ecosystem/EcosystemParts";
import {
  LABEL_WIDTH,
  Lit,
  PartnershipsLayout,
  PlacedGroup,
  clusterOf,
  focusOf,
  layoutClusters,
  layoutPartners,
  litByCluster,
  litByGroup,
  litByPair,
  tieId,
} from "./partnershipsLayout";
import PartnershipsMatrix, { MatrixMeasure, MatrixScale } from "./PartnershipsMatrix";
import {
  ClusterPanel,
  GroupPanel,
  PairPanel,
  PartnersPanel,
  PartnershipsSelection,
} from "./PartnershipsPanel";
import {
  GroupFlowNode,
  GroupMark,
  GroupNodeData,
  GroupNodeView,
  LooseFlowNode,
  LooseLabelView,
  RingGuideFlowNode,
  RingGuideView,
  ShapeFlowNode,
  ShapeNodeData,
  ShapeNodeView,
  ShapeSample,
  TieFlowEdge,
  TieLine,
  TieSample,
  clusterColour,
  groupName,
  strengthText,
  tieReading,
  tieWidth,
} from "./PartnershipsParts";

type CanvasNode = GroupFlowNode | ShapeFlowNode | RingGuideFlowNode | LooseFlowNode;

// Not "group": React Flow styles its own group nodes with a grey box and
// padding, which would shift the mark off the point its ties meet
const NODE_TYPES = {
  org: GroupNodeView,
  shape: ShapeNodeView,
  ring: RingGuideView,
  loose: LooseLabelView,
};
const EDGE_TYPES = { tie: TieLine };
const FIT_VIEW_OPTIONS = { padding: 0.05, maxZoom: 1.15 };
// The width the words over the groups in no cluster are centred in
const LOOSE_LABEL_WIDTH = 200;
// Groups offered as a focus while none is chosen
const SUGGESTION_LIMIT = 24;

// How each mode reads in its switch
export const MODE_LABELS: Record<PartnershipsMode, string> = {
  clusters: "Clusters",
  matrix: "Matrix",
  partners: "Partners of",
};

// How each grain reads in its menu
export const GRAIN_LABELS: Record<PartnershipGrain, string> = {
  coarse: "Coarser",
  medium: "Medium",
  fine: "Finer",
};

const MEASURES: { value: MatrixMeasure; label: string }[] = [
  { value: "strength", label: "Strength" },
  { value: "projects", label: "Shared projects" },
];

// The totals of the scope, as a caption: its groups, its team projects and
// its clusters
export const totalsCaption = (data: Partnerships): string =>
  [
    counted(data.groups, "group"),
    counted(data.team_projects, "team project"),
    counted(data.cluster_count, "cluster"),
  ].join(" · ");

// The floors in force and how much is drawn, as a caption
export const floorsCaption = (data: Partnerships): string =>
  [
    `${data.nodes.length} of ${counted(data.groups, "group")} drawn, at most ${data.floors.top_n}`,
    `modularity ${data.modularity.toFixed(2)}${data.modularity >= 0.3 ? ": a clear structure" : ""}`,
    ...(data.thin_scope
      ? [`the scope holds under ${counted(data.floors.min_scope, "product")}: every number is a hint`]
      : []),
  ].join(" · ");

// The small line beneath a group's name: where it is from, and on a ring
// the strength of its tie to the centre
const captionOf = (entry: PlacedGroup): string | null => {
  const parts = [
    entry.node.country,
    entry.tie !== null ? `tie ${strengthText(entry.tie)}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
};

const LegendEntry: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="li" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    {children}
  </Box>
);

// What the marks of the drawing mean, under it, in plain words: only the
// marks drawn
const Legend: React.FC<{
  data: Partnerships;
  mode: PartnershipsMode;
  measure: MatrixMeasure;
}> = ({ data, mode, measure }) => {
  const theme = useTheme();
  const loose = data.nodes.some((node) => clusterOf(data, node) === null);
  return (
    <Box
      component="ul"
      aria-label="Legend"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 2,
        rowGap: 1,
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      {mode === "clusters" && (
        <>
          <LegendEntry>
            <ShapeSample colour={theme.palette.chart.clusters[0]} />
            shape = a cluster: groups tied more among themselves than chance
          </LegendEntry>
          <LegendEntry>
            <GroupMark colour={theme.palette.chart.clusters[1]} size={12} sample />
            circle = a group (an owner with what it owns), sized by its ties
          </LegendEntry>
          {loose && (
            <LegendEntry>
              <GroupMark colour={theme.palette.chart.unclustered} size={12} sample />
              grey = in no cluster
            </LegendEntry>
          )}
          <LegendEntry>
            <TieSample width={3} />
            line width = strength of the tie; fainter across two clusters
          </LegendEntry>
        </>
      )}
      {mode === "partners" && (
        <>
          <LegendEntry>
            <GroupMark colour={theme.palette.accent.main} size={12} centre sample />
            centre = the organization in focus
          </LegendEntry>
          <LegendEntry>rings = its partners, the strongest tie nearest</LegendEntry>
          <LegendEntry>
            <GroupMark colour={theme.palette.chart.clusters[2]} size={12} sample />
            colour = the partner's cluster
          </LegendEntry>
        </>
      )}
      {mode === "matrix" && (
        <>
          <LegendEntry>
            <MatrixScale data={data} measure={measure} />
          </LegendEntry>
          <LegendEntry>outlined square = a cluster</LegendEntry>
        </>
      )}
      {mode !== "matrix" && (
        <LegendEntry>
          <TieSample lit />
          accent line = the ties of the group in focus
        </LegendEntry>
      )}
    </Box>
  );
};

// The grain of the clusters, as a menu: "Clusters Medium ▾"
const GrainMenu: React.FC<{
  grain: PartnershipGrain;
  onChange: (grain: PartnershipGrain) => void;
}> = ({ grain, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label={`Clusters ${GRAIN_LABELS[grain]}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ ...TOOL_BUTTON_SX, height: 36 }}
      >
        <Box component="span" sx={{ color: "text.secondary", mr: 0.75 }}>
          Clusters
        </Box>
        {GRAIN_LABELS[grain]}
        <Box component="span" sx={{ color: "text.disabled", ml: 0.75 }}>
          ▾
        </Box>
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {PARTNERSHIP_GRAINS.map((value) => (
          <MenuItem
            key={value}
            dense
            selected={value === grain}
            onClick={() => {
              setAnchor(null);
              if (value !== grain) {
                onChange(value);
              }
            }}
          >
            {GRAIN_LABELS[value]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// The number of clusters drawn, as a menu: "Clusters shown 9 of 66 ▾"; the
// number asked for alone while no response tells how many there are
const ClusterCountMenu: React.FC<{
  counts: { shown: number; total: number } | null;
  count: number | null;
  onChange: (count: number | null) => void;
}> = ({ counts, count, onChange }) => {
  const asked = count ?? PARTNERSHIPS_CLUSTERS_DEFAULT;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label="Clusters shown"
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ ...TOOL_BUTTON_SX, height: 36 }}
      >
        <Box component="span" sx={{ color: "text.secondary", mr: 0.75 }}>
          Clusters shown
        </Box>
        {counts
          ? `${counts.shown} of ${counts.total}`
          : asked === PARTNERSHIPS_CLUSTERS_MOST
            ? "All"
            : asked}
        <Box component="span" sx={{ color: "text.disabled", ml: 0.75 }}>
          ▾
        </Box>
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {PARTNERSHIP_CLUSTER_COUNTS.map((value) => (
          <MenuItem
            key={value}
            dense
            selected={value === count}
            onClick={() => {
              setAnchor(null);
              if (value !== count) {
                onChange(value);
              }
            }}
          >
            {value === PARTNERSHIPS_CLUSTERS_MOST ? "All" : value}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// The number of groups drawn of each cluster, as a menu: "Per cluster 8 ▾"
const PerClusterMenu: React.FC<{
  count: number | null;
  onChange: (count: number | null) => void;
}> = ({ count, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = count ?? PARTNERSHIPS_PER_CLUSTER_DEFAULT;
  const label = (value: number) => (value === PARTNERSHIPS_GROUPS_MOST ? "All" : String(value));
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label="Groups per cluster"
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ ...TOOL_BUTTON_SX, height: 36 }}
      >
        <Box component="span" sx={{ color: "text.secondary", mr: 0.75 }}>
          Per cluster
        </Box>
        {label(current)}
        <Box component="span" sx={{ color: "text.disabled", ml: 0.75 }}>
          ▾
        </Box>
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {PARTNERSHIP_PER_CLUSTER_COUNTS.map((value) => (
          <MenuItem
            key={value}
            dense
            selected={value === current}
            onClick={() => {
              setAnchor(null);
              if (value !== current) {
                onChange(value === PARTNERSHIPS_PER_CLUSTER_DEFAULT ? null : value);
              }
            }}
          >
            {label(value)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface CanvasProps {
  data: Partnerships;
  layout: PartnershipsLayout;
  // The partners of one organization, on rings
  partners: boolean;
  selection: PartnershipsSelection;
  onSelect: (selection: PartnershipsSelection) => void;
  // Counts the presses of Fit: each one fits the view again
  fitTick: number;
}

// The clusters or the rings of a layout, the groups and their ties; the
// layout is never run again by a render, only by a new response
const PartnershipsCanvas: React.FC<CanvasProps> = ({
  data,
  layout,
  partners,
  selection,
  onSelect,
  fitTick,
}) => {
  const theme = useTheme();
  // The zoom buttons operate the graph; an exported image has none
  const capturing = useCapturing();
  const { chart } = theme.palette;
  const [nodes, setNodes, onNodesChange] = useNodesState<CanvasNode>([]);
  const { fitView } = useReactFlow<CanvasNode>();
  const measured = useNodesInitialized();
  // The group the pointer or the keyboard is on
  const [pointedId, setPointedId] = useState<string | null>(null);

  const placed = useMemo(
    () => new Map(layout.nodes.map((entry) => [entry.node.id, entry.node])),
    [layout],
  );
  const onGraph = (id: string | null) => (id !== null && placed.has(id) ? id : null);
  // The group whose ties are lit: the one pointed at, else the selected one
  const groupId =
    onGraph(pointedId) ?? (selection?.kind === "group" ? onGraph(selection.id) : null);
  const clusterId = selection?.kind === "cluster" ? selection.id : null;
  const pair = selection?.kind === "pair" ? selection : null;
  const pairA = pair?.a ?? null;
  const pairB = pair?.b ?? null;

  // What is lit: a group's ties, else a cluster's, else a pair's
  const lit = useMemo<Lit | null>(() => {
    if (groupId !== null) {
      return litByGroup(layout, groupId);
    }
    if (clusterId !== null) {
      return litByCluster(layout, clusterId);
    }
    if (pairA !== null && pairB !== null) {
      return litByPair({ a: pairA, b: pairB });
    }
    return null;
  }, [layout, groupId, clusterId, pairA, pairB]);

  const selectGroup = useCallback(
    (id: string) => onSelect({ kind: "group", id }),
    [onSelect],
  );
  const selectCluster = useCallback(
    (id: number) => onSelect({ kind: "cluster", id }),
    [onSelect],
  );

  useEffect(() => {
    const guides: RingGuideFlowNode[] = layout.rings.map((ring) => ({
      id: `ring:${ring.ring}`,
      type: "ring",
      position: { x: -ring.rx, y: -ring.ry },
      data: { ring: ring.ring },
      style: { width: ring.rx * 2, height: ring.ry * 2, pointerEvents: "none" },
      zIndex: -1,
      draggable: false,
      selectable: false,
      focusable: false,
    }));
    const shapes: ShapeFlowNode[] = layout.shapes.map((shape) => ({
      id: `cluster:${shape.cluster.id}`,
      type: "shape",
      position: { x: shape.left, y: shape.top },
      data: {
        cluster: shape.cluster,
        colour: clusterColour(shape.cluster.id, chart),
        path: shape.path,
        box: { left: shape.left, top: shape.top, width: shape.width, height: shape.height },
        labelX: shape.labelX,
        labelWidth: shape.labelWidth,
        lit: false,
        dimmed: false,
        onSelect: selectCluster,
      },
      // The box lets the pointer through to the pane; its outline and its
      // name take it
      style: { width: shape.width, height: shape.height, pointerEvents: "none" },
      zIndex: -1,
      draggable: false,
      selectable: false,
      focusable: false,
    }));
    const loose: LooseFlowNode[] = layout.loose
      ? [
          {
            id: "loose",
            type: "loose",
            position: { x: layout.loose.x - LOOSE_LABEL_WIDTH / 2, y: layout.loose.y },
            data: {},
            style: { width: LOOSE_LABEL_WIDTH, pointerEvents: "none" },
            draggable: false,
            selectable: false,
            focusable: false,
          },
        ]
      : [];
    // A group is placed by the middle of its mark; its name hangs beneath
    const groups: GroupFlowNode[] = layout.nodes.map((entry) => ({
      id: entry.node.id,
      type: "org",
      position: { x: entry.x - LABEL_WIDTH / 2, y: entry.y - entry.size / 2 },
      data: {
        node: entry.node,
        size: entry.size,
        colour: clusterColour(entry.clusters[0] ?? null, chart),
        // In the clusters, the colours of the other clusters it belongs to
        also:
          layout.shapes.length > 0
            ? entry.clusters.slice(1).map((cluster) => clusterColour(cluster, chart))
            : [],
        centre: entry.ring === 0,
        caption: captionOf(entry),
        selected: false,
        dimmed: false,
        onSelect: selectGroup,
        onPoint: setPointedId,
      },
      draggable: false,
      connectable: false,
    }));
    setNodes([...guides, ...shapes, ...loose, ...groups]);
  }, [layout, data, chart, selectGroup, selectCluster, setNodes]);

  // The view is fitted once the names are measured, on every new layout
  // and on every press of Fit
  useEffect(() => {
    if (measured) {
      void fitView(FIT_VIEW_OPTIONS);
    }
  }, [measured, layout, fitTick, fitView]);

  // A change of selection or of the group pointed at lights and dims in
  // place
  const selectedGroup = selection?.kind === "group" ? selection.id : null;
  useEffect(() => {
    setNodes((current) =>
      current.map((node) => {
        if (node.type === "org") {
          const data = node.data as GroupNodeData;
          const selected = node.id === selectedGroup;
          const dimmed = lit !== null && !lit.nodes.has(node.id) && !selected;
          return data.selected === selected && data.dimmed === dimmed
            ? node
            : { ...node, data: { ...data, selected, dimmed } };
        }
        if (node.type === "shape") {
          const data = node.data as ShapeNodeData;
          const isLit = clusterId === data.cluster.id;
          const dimmed = clusterId !== null && !isLit;
          return data.lit === isLit && data.dimmed === dimmed
            ? node
            : { ...node, data: { ...data, lit: isLit, dimmed } };
        }
        return node;
      }),
    );
  }, [layout, selectedGroup, clusterId, lit, setNodes]);

  const edges = useMemo<TieFlowEdge[]>(() => {
    const most = Math.max(0, ...layout.ties.map((tie) => tie.pair.strength));
    const name = (id: string) => {
      const node = placed.get(id);
      return node ? groupName(node) : id;
    };
    const focusId = partners ? (focusOf(data)?.id ?? null) : null;
    // A spoke selects the partner at its far end; a tie between two
    // partners selects nothing
    const selectTie = (spoke: boolean) =>
      partners
        ? spoke
          ? (tied: PartnershipPair) =>
              onSelect({ kind: "group", id: tied.a === focusId ? tied.b : tied.a })
          : null
        : (tied: PartnershipPair) => onSelect({ kind: "pair", a: tied.a, b: tied.b });
    const drawn = layout.ties.map((tie): TieFlowEdge => {
      const active = lit?.direct.has(tie.id) ?? false;
      const route = lit?.route.has(tie.id) ?? false;
      return {
        id: tie.id,
        type: "tie",
        source: tie.pair.a,
        target: tie.pair.b,
        selectable: false,
        focusable: false,
        data: {
          x1: tie.x1,
          y1: tie.y1,
          x2: tie.x2,
          y2: tie.y2,
          pair: tie.pair,
          width: tieWidth(tie.pair.strength, most),
          inside: tie.inside,
          spoke: tie.spoke,
          reading: tieReading(tie.pair, name(tie.pair.a), name(tie.pair.b)),
          active,
          route,
          dimmed: lit !== null && !active && !route,
          hidden: partners && !tie.spoke,
          onSelect: selectTie(tie.spoke),
        },
      };
    });
    // The lit ties are drawn over the rest
    return [
      ...drawn.filter((edge) => !edge.data?.active && !edge.data?.route),
      ...drawn.filter((edge) => edge.data?.active || edge.data?.route),
    ];
  }, [layout, placed, data, partners, lit, onSelect]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      onPaneClick={() => onSelect(null)}
      nodesDraggable={false}
      nodesConnectable={false}
      fitView
      fitViewOptions={FIT_VIEW_OPTIONS}
      minZoom={0.15}
      maxZoom={1.5}
      colorMode={theme.palette.mode}
      proOptions={{ hideAttribution: true }}
    >
      {!capturing && <Controls showInteractive={false} showFitView={false} />}
    </ReactFlow>
  );
};

const isNotFound = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 404;

// Whether a selection still names something of the response
const stillThere = (data: Partnerships, selection: PartnershipsSelection): boolean => {
  switch (selection?.kind) {
    case undefined:
      return true;
    case "cluster":
      return data.clusters.some((cluster) => cluster.id === selection.id);
    case "group":
      return data.nodes.some((node) => node.id === selection.id);
    case "pair":
      return data.pairs.some((pair) => tieId(pair) === tieId(selection));
  }
};

// A centred message in the canvas
const Message: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}>{children}</Box>
);

// The partnerships: which organizations work together on the products of
// the scope. Groups (an owner with what it owns) are drawn in their
// clusters, as a matrix of their pairs, or round one organization with its
// partners on rings.
const PartnershipsView: React.FC<AnalyticsPaneProps> = ({ state, onChange, onOpenEntity }) => {
  const settings = state.settings.partnerships;
  const mode = settings.mode;
  const params = useMemo(() => toPartnershipsParams(state), [state]);
  const focusing = params.focus_id !== undefined;
  const read = usePartnerships(params);
  const data: Partnerships | null = read.data ?? null;
  const [selection, setSelection] = useState<PartnershipsSelection>(null);
  const [measure, setMeasure] = useState<MatrixMeasure>("strength");
  const [fitTick, setFitTick] = useState(0);

  const layout = useMemo(
    () =>
      !data || mode === "matrix" || (mode === "partners" && !focusing)
        ? null
        : focusing
          ? layoutPartners(data)
          : layoutClusters(data),
    [data, mode, focusing],
  );

  // A new mode, grain or focus starts with nothing selected; a new scope
  // keeps what it still holds
  useEffect(() => {
    setSelection(null);
  }, [mode, settings.grain, settings.focusId]);
  useEffect(() => {
    if (data && !stillThere(data, selection)) {
      setSelection(null);
    }
  }, [data, selection]);

  const setSettings = useCallback(
    (next: PartnershipsSettings) => {
      onChange({
        settings: {
          ...state.settings,
          partnerships: changePartnershipsSettings(settings, next),
        },
      });
    },
    [onChange, state.settings, settings],
  );
  const expandCluster = useCallback(
    (expand: number | null) => setSettings({ ...settings, expand }),
    [setSettings, settings],
  );
  const showPartners = useCallback(
    (id: string) => setSettings({ ...settings, mode: "partners", focusId: id }),
    [setSettings, settings],
  );
  const backToClusters = useCallback(
    () => setSettings({ ...settings, mode: "clusters", focusId: null }),
    [setSettings, settings],
  );

  const focus = focusing && data ? focusOf(data) : null;
  const focusName = focus ? groupName(focus) : (data?.focus?.name ?? settings.focusId ?? "");
  // The groups a focus may be picked from: the response's, the focus aside
  const options = useMemo<PartnershipNode[]>(
    () => (data ? data.nodes.filter((node) => node.id !== focus?.id) : []),
    [data, focus],
  );
  const suggested = useMemo<PartnershipNode[]>(
    () =>
      data && !focusing
        ? [...data.nodes].sort((a, b) => b.strength - a.strength).slice(0, SUGGESTION_LIMIT)
        : [],
    [data, focusing],
  );
  const empty = data !== null && (data.pairs.length === 0 || data.nodes.length === 0);
  const graph = layout !== null && !empty;

  const selectedNode =
    data && selection?.kind === "group"
      ? (data.nodes.find((node) => node.id === selection.id) ?? null)
      : null;
  const selectedCluster =
    data && selection?.kind === "cluster"
      ? (data.clusters.find((cluster) => cluster.id === selection.id) ?? null)
      : null;
  const selectedPair =
    data && selection?.kind === "pair"
      ? (data.pairs.find((pair) => tieId(pair) === tieId(selection)) ?? null)
      : null;

  // In an exported image: the settings said in words, in place of the
  // controls that set them, and no panel
  const capturing = useCapturing();
  const facts: [string, string][] = [
    ["Mode", MODE_LABELS[mode]],
    ...(focusing ? [["Partners of", focusName] as [string, string]] : []),
    ...(mode === "matrix"
      ? [["Cell", MEASURES.find((entry) => entry.value === measure)?.label ?? measure] as [string, string]]
      : []),
    ["Clusters", GRAIN_LABELS[settings.grain]],
    ...(mode !== "partners" && data
      ? [["Clusters shown", `${data.clusters.length} of ${data.cluster_count}`] as [string, string]]
      : []),
  ];

  // The panel of what is selected, raised over the clusters or the matrix
  const overlay =
    data &&
    !capturing &&
    (selectedCluster ? (
      <ClusterPanel
        data={data}
        cluster={selectedCluster}
        expanded={settings.expand === selectedCluster.id}
        onExpand={expandCluster}
        onSelect={setSelection}
        onOpenEntity={onOpenEntity}
      />
    ) : selectedNode ? (
      <GroupPanel
        data={data}
        node={selectedNode}
        onSelect={setSelection}
        onShowPartners={showPartners}
        onOpenEntity={onOpenEntity}
      />
    ) : selectedPair ? (
      <PairPanel
        data={data}
        pair={selectedPair}
        onSelect={setSelection}
        onShowPartners={showPartners}
        onOpenEntity={onOpenEntity}
      />
    ) : null);

  return (
    <Box
      component="section"
      aria-label={ANALYTICS_VIEW_LABELS.partnerships}
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.75 }}
    >
      {capturing ? (
        <ExportFacts aria-label="Settings" facts={facts} />
      ) : (
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            aria-label="Mode"
            onChange={(_event, next: PartnershipsMode | null) => {
              if (next) {
                setSettings({ ...settings, mode: next });
              }
            }}
          >
            {PARTNERSHIPS_MODES.map((value) => (
              <ToggleButton key={value} value={value} sx={{ ...SEGMENT_SX, height: 36 }}>
                {MODE_LABELS[value]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {mode === "partners" && (
            <Autocomplete<PartnershipNode>
              size="small"
              sx={{
                flex: "0 1 400px",
                minWidth: 260,
                "& .MuiOutlinedInput-root": {
                  minHeight: 36,
                  py: "0 !important",
                  flexWrap: "nowrap",
                  gap: 1,
                  fontSize: 13,
                  backgroundColor: "background.paper",
                },
                "& .MuiOutlinedInput-root .MuiAutocomplete-input": { py: 0, minWidth: 60 },
              }}
              options={options}
              value={null}
              getOptionLabel={groupName}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="No group in this scope"
              onChange={(_event, picked) => {
                if (picked) {
                  showPartners(picked.id);
                }
              }}
              renderInput={(inputParams) => (
                <TextField
                  {...inputParams}
                  placeholder={focusing ? "Another organization…" : "Type a name"}
                  inputProps={{ ...inputParams.inputProps, "aria-label": "Organization in focus" }}
                  InputProps={{
                    ...inputParams.InputProps,
                    // The field names itself and holds the focus in force
                    startAdornment: (
                      <>
                        <Box
                          component="span"
                          aria-hidden
                          sx={{
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 500,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Partners of
                        </Box>
                        {focusing && (
                          <Chip
                            label={focusName}
                            onDelete={backToClusters}
                            size="small"
                            aria-label="Current focus"
                            sx={{ maxWidth: 220 }}
                          />
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {mode === "partners" && (
            <Button variant="outlined" size="small" onClick={backToClusters} sx={{ ...TOOL_BUTTON_SX, height: 36 }}>
              ← Back to clusters
            </Button>
          )}
          {mode === "matrix" && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={measure}
              aria-label="Cell"
              onChange={(_event, next: MatrixMeasure | null) => {
                if (next) {
                  setMeasure(next);
                }
              }}
            >
              {MEASURES.map(({ value, label }) => (
                <ToggleButton key={value} value={value} sx={{ ...SEGMENT_SX, height: 36 }}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <GrainMenu grain={settings.grain} onChange={(grain) => setSettings({ ...settings, grain })} />
          {mode !== "partners" && (
            <ClusterCountMenu
              counts={data ? { shown: data.clusters.length, total: data.cluster_count } : null}
              count={settings.clusters}
              onChange={(clusters) => setSettings({ ...settings, clusters })}
            />
          )}
          {mode !== "partners" && (
            <PerClusterMenu
              count={settings.perCluster}
              onChange={(perCluster) => setSettings({ ...settings, perCluster })}
            />
          )}
          {mode !== "matrix" && (
            <Button
              variant="outlined"
              size="small"
              disabled={!graph}
              onClick={() => setFitTick((tick) => tick + 1)}
              sx={{ ...TOOL_BUTTON_SX, ml: "auto" }}
            >
              Fit
            </Button>
          )}
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          minHeight: 560,
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        {!data ? (
          <Message>
            {read.isLoading ? (
              <LogoLoader size="view" surface="paper" label="Loading the partnerships" />
            ) : isNotFound(read.error) ? (
              <Box sx={{ textAlign: "center" }}>
                <Typography color="text.secondary" gutterBottom>
                  Organization not found: {settings.focusId}
                </Typography>
                <Typography variant="caption" component="div" color="text.disabled" gutterBottom>
                  It is unknown, or a public body, which works in no group.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={backToClusters}
                  sx={{ textTransform: "none", mt: 1 }}
                >
                  Back to clusters
                </Button>
              </Box>
            ) : (
              <Typography color="text.secondary">The partnerships could not be loaded.</Typography>
            )}
          </Message>
        ) : empty ? (
          <Message>
            <Typography color="text.secondary" align="center" sx={{ maxWidth: 520 }}>
              {focusing
                ? `No partner of ${focusName} in this scope: it shares no project, product or stated partnership with another group here.`
                : `No two organizations work together in this scope: the ${counted(
                    data.evidence.n,
                    "product",
                  )} in it share no project, product or stated partnership between groups.`}
            </Typography>
          </Message>
        ) : mode === "partners" && !focusing ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 3,
            }}
          >
            <Typography color="text.secondary" align="center">
              Pick an organization to draw its partners: one of the strongest groups of this
              scope below, or any group by name in the field above.
            </Typography>
            <Box
              role="list"
              aria-label="Suggested organizations"
              sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", maxWidth: 720 }}
            >
              {suggested.map((node) => (
                <Box component="span" role="listitem" key={node.id}>
                  <Chip
                    label={groupName(node)}
                    size="small"
                    variant="outlined"
                    onClick={() => showPartners(node.id)}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ) : mode === "matrix" ? (
          <>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "auto",
                p: 2,
                // Room for the panel, so that it covers no cell
                pr: overlay ? "340px" : 2,
              }}
            >
              <PartnershipsMatrix
                data={data}
                measure={measure}
                selection={selection}
                onSelect={setSelection}
              />
            </Box>
            {overlay}
          </>
        ) : layout ? (
          <>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                position: "relative",
                // The surface shows through: no second ground under the graph
                "& .react-flow": { backgroundColor: "transparent" },
              }}
            >
              <ReactFlowProvider>
                <PartnershipsCanvas
                  data={data}
                  layout={layout}
                  partners={focusing}
                  selection={selection}
                  onSelect={setSelection}
                  fitTick={fitTick}
                />
              </ReactFlowProvider>
              {!focusing && overlay}
            </Box>
            {focusing && focus && !capturing && (
              <PartnersPanel
                data={data}
                focus={focus}
                selectedId={selection?.kind === "group" ? selection.id : null}
                onSelect={(id) => setSelection(id === null ? null : { kind: "group", id })}
                onShowPartners={showPartners}
                onOpenEntity={onOpenEntity}
              />
            )}
          </>
        ) : null}
      </Box>

      {data && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {!empty && !(mode === "partners" && !focusing) && (
            <Legend data={data} mode={mode} measure={measure} />
          )}
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            aria-label="Totals"
            sx={monospaceStyle}
          >
            {totalsCaption(data)}
          </Typography>
          <Typography variant="caption" component="div" color="text.disabled" aria-label="Floors">
            {floorsCaption(data)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PartnershipsView;
